// Implements: F-GEN-007, F-UIX-010
/**
 * Following a generation run: the one place that knows how.
 *
 * A conversation turn follows the generation `/stream`, and faces two facts: the
 * server records the run a moment AFTER the client learns its id (so a `404` is
 * waiting, not an error), and a stream can drop. The reconnect budget counts
 * CONSECUTIVE failures: a connection that delivered at least one event was healthy,
 * so it gives the budget back, and a long run that is reconnected thirty times is
 * not "interrupted". A `429` (the per-minute limit) is a wait, not a failure: the next
 * attempt comes after the `Retry-After` the server sent (at most `MAX_STREAM_WAIT_S`), and it
 * spends no budget for the first `MAX_RATE_LIMIT_WAITS` in a row.
 *
 * Stopping a turn is not here: it is its own conversation route and has no run to wait for.
 */

import { abortableDelay as wait } from "./abortableDelay";
import { ApiError } from "./ApiError";
import { parseRetryAfter } from "./rateLimit";

/** How the stream is retried while the server has not recorded the run yet. */
export interface StreamRetry {
  readonly delayMs: number;
  readonly attempts: number;
}

/** How far the pause between two polls may grow, in times the base delay. */
const MAX_BACKOFF_FACTOR = 16;

/** The pause before poll number `attempt` (from 0): doubles each time, up to a bounded ceiling. */
export function backoffDelay(retry: StreamRetry, attempt: number): number {
  return Math.min(retry.delayMs * 2 ** attempt, retry.delayMs * MAX_BACKOFF_FACTOR);
}

export const DEFAULT_STREAM_RETRY: StreamRetry = { delayMs: 400, attempts: 25 };

/**
 * The longest single wait a `429` may ask for, in seconds. The backend's per-minute limit
 * never asks more than 60; a larger value is a broken or hostile header, and above about
 * 24.8 days `setTimeout` overflows and fires at once, turning the wait into a tight loop.
 */
export const MAX_STREAM_WAIT_S = 60;

/** How many `429` waits in a row are free; the next one counts as a failure of the budget. */
export const MAX_RATE_LIMIT_WAITS = 10;

export interface FollowOptions<E> {
  /** Opens (or reopens) the stream. */
  readonly open: () => AsyncGenerator<E, void, undefined>;
  readonly onEvent: (event: E) => void;
  /** The run has ended by whatever means: nothing more to follow. */
  readonly isDone: () => boolean;
  readonly signal: AbortSignal;
  readonly retry: StreamRetry;
  /**
   * A stream the server closed normally with no final state: `true` opens it again
   * (a turn has nobody else to say how it ended), `false` returns (the one-shot
   * generation is ended by its own POST).
   */
  readonly reopenOnClose: boolean;
  /** The pause between two attempts; tests inject one that records instead of sleeping. */
  readonly pause?: ((ms: number, signal: AbortSignal) => Promise<void>) | undefined;
}

/** Follow until the run is done, the signal aborts, or the failures in a row exhaust the budget. */
export async function followStream<E>(options: FollowOptions<E>): Promise<void> {
  const { open, onEvent, isDone, signal, retry, reopenOnClose, pause = wait } = options;
  let failures = 0;
  // 429 waits in a row: past `MAX_RATE_LIMIT_WAITS` a 429 spends the budget like any failure,
  // so a server that never stops limiting ends in "interrupted" instead of "running" forever.
  let waits = 0;
  // A function: TypeScript narrows the property after the loop test and cannot see the abort
  // that happens while the stream is being read.
  const stopped = (): boolean => signal.aborted;
  while (failures < retry.attempts && !stopped() && !isDone()) {
    let delivered = false;
    let waitMs: number | null = null;
    try {
      for await (const event of open()) {
        delivered = true;
        onEvent(event);
      }
      if (isDone() || !reopenOnClose) return;
    } catch (cause) {
      if (stopped()) return;
      // A refusal that waiting cannot fix: do not hammer the server.
      if (cause instanceof ApiError && (cause.status === 401 || cause.status === 403)) return;
      // The per-minute limit (PLT.6): a wait the server sized, not a failure. Asking again
      // every 400 ms would only spend the next minute's allowance.
      if (cause instanceof ApiError && cause.status === 429) {
        waitMs = Math.min(parseRetryAfter(cause.headers.get("Retry-After")), MAX_STREAM_WAIT_S) * 1000;
      }
    }
    waits = waitMs === null ? 0 : waits + 1;
    if (waitMs === null || waits > MAX_RATE_LIMIT_WAITS) failures = delivered ? 0 : failures + 1;
    await pause(waitMs ?? retry.delayMs, signal);
  }
}
